import axios from 'axios';
import { API_BASE_URL } from '../config/api';

// Create axios instance with production-ready defaults
const axiosInstance = axios.create({
  baseURL: API_BASE_URL, // e.g., https://ecommerce-api-staging.up.railway.app
  withCredentials: true, // Ensure cookies are sent with requests
  timeout: 30000, // Increased timeout for production reliability
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  // Validate status codes to handle all responses manually
  validateStatus: (status) => status >= 200 && status < 600, // Include 5xx errors
});

// Request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    // Handle retries configuration
    config.retry = config.retry || {
      attempts: 3, // Increased to 3 attempts for 502 errors
      delay: 1000, // Initial delay in ms
    };

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor with retry logic
axiosInstance.interceptors.response.use(
  (response) => {
    // Return data directly for successful responses
    if (response.status >= 200 && response.status < 300) {
      return response.data;
    }
    // Erros de cliente/servidor: um Error com a resposta anexada, para que as
    // páginas leiam `err.response.data.message` como leem nos erros do axios.
    const error = new Error(
      response.data?.message || response.data?.error || `Request failed with status ${response.status}`
    );
    error.response = response;
    error.status = response.status;
    error.data = response.data;
    throw error;
  },
  async (error) => {
    const originalRequest = error.config;

    // Retry apenas em GET: repetir um POST (ex.: criar encomenda) duplicava o
    // pedido no servidor. Se algum POST for repetido, o `X-Idempotency-Key` do
    // CheckoutPage viaja no próprio config e é reenviado intacto aqui.
    if (
      originalRequest.method === 'get' &&
      (!error.response || error.response.status === 502) &&
      originalRequest.retry.attempts > 0
    ) {
      console.warn(`Retrying request to ${originalRequest.url}. Attempts left: ${originalRequest.retry.attempts}`);
      await new Promise((resolve) => setTimeout(resolve, originalRequest.retry.delay));
      originalRequest.retry.attempts -= 1;
      originalRequest.retry.delay *= 2; // Exponential backoff
      return axiosInstance(originalRequest);
    }

    // Handle specific status codes
    if (error.response) {
      const { status } = error.response;

      // Unauthorized - redirect to login
      if (status === 401) {
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }

      // Too Many Requests - inform user
      if (status === 429) {
        throw new Error('Too many requests. Please try again later.');
      }

      // Bad Gateway - inform user after retries are exhausted
      if (status === 502) {
        throw new Error('Server is temporarily unavailable. Please try again later.');
      }
    }

    // Throw detailed error for debugging
    const errorMessage = error.response?.data?.error || error.message || 'An unexpected error occurred';
    const detailedError = new Error(errorMessage);
    detailedError.status = error.response?.status;
    detailedError.data = error.response?.data;
    throw detailedError;
  }
);

// Utility functions for common HTTP methods
export const api = {
  get: (url, config = {}) => axiosInstance.get(url, config),
  post: (url, data, config = {}) => axiosInstance.post(url, data, config),
  put: (url, data, config = {}) => axiosInstance.put(url, data, config),
  delete: (url, config = {}) => axiosInstance.delete(url, config),
  patch: (url, data, config = {}) => axiosInstance.patch(url, data, config),
};

export default api;